/**
 * Export Utilities for Financial Reports
 * Provides PDF, Excel, and Print functionality
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

/**
 * Format currency for export (string format)
 */
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount);
};

/**
 * Format date for display
 */
const formatDate = (dateInput) => {
  if (!dateInput) return '';
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

/**
 * Format date for filename
 */
const formatDateForFilename = (dateInput) => {
  if (!dateInput) return new Date().toISOString().split('T')[0];
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  return date.toISOString().split('T')[0];
};

/**
 * Detect if we're running inside a sandboxed iframe (Emergent preview).
 * On deployed sites (user's own domain), this returns false and we can
 * use direct download/print. In the preview, we use fallback methods.
 */
const isEmbedded = () => {
  try { return window.self !== window.top; } catch (e) { return true; }
};

/**
 * Force download a file.
 * - Deployed site: Direct <a download>.click() (works natively)
 * - Preview iframe: Dispatches 'download-ready' event for the PDF viewer overlay
 */
const forceDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const type = filename?.endsWith('.pdf') ? 'pdf' : filename?.endsWith('.xlsx') ? 'excel' : 'file';

  if (!isEmbedded()) {
    // Direct download — works on deployed sites (no sandbox)
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 250);
  } else {
    // Sandboxed iframe — show PDF viewer overlay
    window.dispatchEvent(new CustomEvent('download-ready', {
      detail: { url, filename, type }
    }));
  }
};

/**
 * Save PDF file - converts to blob and forces download
 */
const savePDF = (doc, filename) => {
  try {
    console.log('Saving PDF:', filename);
    
    // Get PDF as blob
    const pdfBlob = doc.output('blob');
    console.log('PDF blob created, size:', pdfBlob.size);
    
    // Force download using anchor element
    forceDownload(pdfBlob, filename);
    
    console.log('PDF download triggered');
    return true;
  } catch (error) {
    console.error('PDF save error:', error);
    alert('Failed to save PDF: ' + error.message);
    return false;
  }
};

/**
 * Save Excel file - converts to blob and forces download
 */
const saveExcelFile = (wb, filename) => {
  try {
    console.log('Saving Excel:', filename);
    
    // Write to array buffer
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    
    // Create blob
    const blob = new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    console.log('Excel blob created, size:', blob.size);
    
    // Force download using anchor element
    forceDownload(blob, filename);
    
    console.log('Excel download triggered');
    return true;
  } catch (error) {
    console.error('Excel save error:', error);
    alert('Failed to export Excel file: ' + error.message);
    return false;
  }
};

/**
 * Apply currency format to Excel cells
 */
const applyCurrencyFormat = (ws, currencyColumns, startRow = 0) => {
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  
  for (let row = startRow; row <= range.e.r; row++) {
    for (const col of currencyColumns) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
      const cell = ws[cellAddress];
      if (cell && typeof cell.v === 'number') {
        cell.z = '"$"#,##0.00';
      }
    }
  }
};

// Professional color scheme for PDF - readable on all backgrounds
const PDF_COLORS = {
  headerBg: [55, 65, 81],       // Dark gray for main headers
  headerText: [255, 255, 255],  // White text on dark headers
  sectionBg: [75, 85, 99],      // Medium gray for section headers
  sectionText: [255, 255, 255], // White text on section headers
  totalBg: [243, 244, 246],     // Light gray for total rows
  totalText: [17, 24, 39],      // Dark text on light total rows
  grandTotalBg: [31, 41, 55],   // Very dark for grand totals
  grandTotalText: [255, 255, 255], // White on grand totals
  bodyText: [31, 41, 55],       // Dark text for body
  altRowBg: [249, 250, 251]     // Very light gray for alternating rows
};

// Standard column widths for consistent alignment
const COLUMN_WIDTHS = {
  accountName: 120,
  amount: 55,
  total: 175  // accountName + amount
};

/**
 * Export P&L report to PDF
 */
export const exportPnLToPDF = ({
  companyName,
  startDate,
  endDate,
  basis,
  incomeAccounts = [],
  expenseAccounts = [],
  totalIncome = 0,
  totalExpenses = 0,
  netIncome = 0
}) => {
  try {
    console.log('exportPnLToPDF called with:', { companyName, startDate, endDate, basis, incomeAccounts: incomeAccounts?.length, expenseAccounts: expenseAccounts?.length, totalIncome, totalExpenses, netIncome });
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...PDF_COLORS.bodyText);
    doc.text(companyName || 'Company', 14, 20);
    
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Profit & Loss Statement', 14, 30);
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`${formatDate(startDate)} to ${formatDate(endDate)}`, 14, 38);
    doc.text(`Basis: ${basis === 'cash' ? 'Cash' : 'Accrual'}`, 14, 44);
    
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - 60, 20);
    doc.setTextColor(...PDF_COLORS.bodyText);
    
    let startY = 52;
    
    // Column headers
    autoTable(doc, {
      startY: startY,
      head: [['Account', 'Amount']],
      body: [],
      theme: 'plain',
      styles: { fontSize: 9, textColor: PDF_COLORS.bodyText },
      headStyles: { 
        fillColor: PDF_COLORS.headerBg, 
        textColor: PDF_COLORS.headerText, 
        fontStyle: 'bold',
        halign: 'left'
      },
      columnStyles: { 
        0: { cellWidth: COLUMN_WIDTHS.accountName }, 
        1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
      }
    });
    
    startY = doc.lastAutoTable.finalY;
    
    // Income Section Header
    autoTable(doc, {
      startY: startY,
      body: [['INCOME', '']],
      theme: 'plain',
      styles: { fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fillColor: PDF_COLORS.sectionBg, textColor: PDF_COLORS.sectionText },
      columnStyles: { 0: { cellWidth: COLUMN_WIDTHS.total } }
    });
    
    startY = doc.lastAutoTable.finalY;
    
    // Income accounts
    if (incomeAccounts.length > 0) {
      autoTable(doc, {
        startY: startY,
        body: incomeAccounts.map(acc => [
          acc.account_name || acc.name || 'Unknown',
          formatCurrency(acc.balance || acc.amount || 0)
        ]),
        theme: 'striped',
        styles: { fontSize: 9, textColor: PDF_COLORS.bodyText },
        alternateRowStyles: { fillColor: PDF_COLORS.altRowBg },
        columnStyles: { 
          0: { cellWidth: COLUMN_WIDTHS.accountName }, 
          1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
        }
      });
      startY = doc.lastAutoTable.finalY;
    }
    
    // Total Income
    autoTable(doc, {
      startY: startY,
      body: [['Total Income', formatCurrency(totalIncome)]],
      theme: 'plain',
      styles: { fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fillColor: PDF_COLORS.totalBg, textColor: PDF_COLORS.totalText },
      columnStyles: { 
        0: { cellWidth: COLUMN_WIDTHS.accountName }, 
        1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
      }
    });
    
    startY = doc.lastAutoTable.finalY + 5;
    
    // Expenses Section Header
    autoTable(doc, {
      startY: startY,
      body: [['EXPENSES', '']],
      theme: 'plain',
      styles: { fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fillColor: PDF_COLORS.sectionBg, textColor: PDF_COLORS.sectionText },
      columnStyles: { 0: { cellWidth: COLUMN_WIDTHS.total } }
    });
    
    startY = doc.lastAutoTable.finalY;
    
    // Expense accounts
    if (expenseAccounts.length > 0) {
      autoTable(doc, {
        startY: startY,
        body: expenseAccounts.map(acc => [
          acc.account_name || acc.name || 'Unknown',
          formatCurrency(acc.balance || acc.amount || 0)
        ]),
        theme: 'striped',
        styles: { fontSize: 9, textColor: PDF_COLORS.bodyText },
        alternateRowStyles: { fillColor: PDF_COLORS.altRowBg },
        columnStyles: { 
          0: { cellWidth: COLUMN_WIDTHS.accountName }, 
          1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
        }
      });
      startY = doc.lastAutoTable.finalY;
    }
    
    // Total Expenses
    autoTable(doc, {
      startY: startY,
      body: [['Total Expenses', formatCurrency(totalExpenses)]],
      theme: 'plain',
      styles: { fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fillColor: PDF_COLORS.totalBg, textColor: PDF_COLORS.totalText },
      columnStyles: { 
        0: { cellWidth: COLUMN_WIDTHS.accountName }, 
        1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
      }
    });
    
    startY = doc.lastAutoTable.finalY + 8;
    
    // Net Income
    autoTable(doc, {
      startY: startY,
      body: [['NET INCOME', formatCurrency(netIncome)]],
      theme: 'plain',
      styles: { fontStyle: 'bold', fontSize: 11 },
      bodyStyles: { fillColor: PDF_COLORS.grandTotalBg, textColor: PDF_COLORS.grandTotalText },
      columnStyles: { 
        0: { cellWidth: COLUMN_WIDTHS.accountName }, 
        1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
      }
    });
    
    // Open PDF in new tab - user can save/print from browser
    const filename = `ProfitLoss_${formatDateForFilename(startDate)}_${formatDateForFilename(endDate)}.pdf`;
    return savePDF(doc, filename);
  } catch (error) {
    console.error('PDF Export Error:', error);
    alert('Failed to export PDF: ' + error.message);
    return false;
  }
};

/**
 * Export Balance Sheet to PDF - Fixed column alignment
 */
export const exportBalanceSheetToPDF = ({
  companyName,
  asOfDate,
  basis,
  assets = [],
  liabilities = [],
  equity = [],
  totalAssets = 0,
  totalLiabilities = 0,
  totalEquity = 0
}) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...PDF_COLORS.bodyText);
    doc.text(companyName || 'Company', 14, 20);
    
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Balance Sheet', 14, 30);
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`As of ${formatDate(asOfDate)}`, 14, 38);
    doc.text(`Basis: ${basis === 'cash' ? 'Cash' : 'Accrual'}`, 14, 44);
    
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - 60, 20);
    doc.setTextColor(...PDF_COLORS.bodyText);
    
    let startY = 52;
    
    // Column headers
    autoTable(doc, {
      startY: startY,
      head: [['Account', 'Balance']],
      body: [],
      theme: 'plain',
      styles: { fontSize: 9, textColor: PDF_COLORS.bodyText },
      headStyles: { 
        fillColor: PDF_COLORS.headerBg, 
        textColor: PDF_COLORS.headerText, 
        fontStyle: 'bold'
      },
      columnStyles: { 
        0: { cellWidth: COLUMN_WIDTHS.accountName }, 
        1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
      }
    });
    
    startY = doc.lastAutoTable.finalY;
    
    // ASSETS Section
    autoTable(doc, {
      startY: startY,
      body: [['ASSETS', '']],
      theme: 'plain',
      styles: { fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fillColor: PDF_COLORS.sectionBg, textColor: PDF_COLORS.sectionText },
      columnStyles: { 0: { cellWidth: COLUMN_WIDTHS.total } }
    });
    startY = doc.lastAutoTable.finalY;
    
    if (assets.length > 0) {
      autoTable(doc, {
        startY: startY,
        body: assets.map(acc => [acc.account_name || acc.name || 'Unknown', formatCurrency(acc.balance || 0)]),
        theme: 'striped',
        styles: { fontSize: 9, textColor: PDF_COLORS.bodyText },
        alternateRowStyles: { fillColor: PDF_COLORS.altRowBg },
        columnStyles: { 
          0: { cellWidth: COLUMN_WIDTHS.accountName }, 
          1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
        }
      });
      startY = doc.lastAutoTable.finalY;
    }
    
    // Total Assets
    autoTable(doc, {
      startY: startY,
      body: [['Total Assets', formatCurrency(totalAssets)]],
      theme: 'plain',
      styles: { fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fillColor: PDF_COLORS.totalBg, textColor: PDF_COLORS.totalText },
      columnStyles: { 
        0: { cellWidth: COLUMN_WIDTHS.accountName }, 
        1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
      }
    });
    startY = doc.lastAutoTable.finalY + 5;
    
    // LIABILITIES Section
    autoTable(doc, {
      startY: startY,
      body: [['LIABILITIES', '']],
      theme: 'plain',
      styles: { fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fillColor: PDF_COLORS.sectionBg, textColor: PDF_COLORS.sectionText },
      columnStyles: { 0: { cellWidth: COLUMN_WIDTHS.total } }
    });
    startY = doc.lastAutoTable.finalY;
    
    if (liabilities.length > 0) {
      autoTable(doc, {
        startY: startY,
        body: liabilities.map(acc => [acc.account_name || acc.name || 'Unknown', formatCurrency(acc.balance || 0)]),
        theme: 'striped',
        styles: { fontSize: 9, textColor: PDF_COLORS.bodyText },
        alternateRowStyles: { fillColor: PDF_COLORS.altRowBg },
        columnStyles: { 
          0: { cellWidth: COLUMN_WIDTHS.accountName }, 
          1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
        }
      });
      startY = doc.lastAutoTable.finalY;
    }
    
    // Total Liabilities
    autoTable(doc, {
      startY: startY,
      body: [['Total Liabilities', formatCurrency(totalLiabilities)]],
      theme: 'plain',
      styles: { fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fillColor: PDF_COLORS.totalBg, textColor: PDF_COLORS.totalText },
      columnStyles: { 
        0: { cellWidth: COLUMN_WIDTHS.accountName }, 
        1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
      }
    });
    startY = doc.lastAutoTable.finalY + 5;
    
    // EQUITY Section
    autoTable(doc, {
      startY: startY,
      body: [['EQUITY', '']],
      theme: 'plain',
      styles: { fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fillColor: PDF_COLORS.sectionBg, textColor: PDF_COLORS.sectionText },
      columnStyles: { 0: { cellWidth: COLUMN_WIDTHS.total } }
    });
    startY = doc.lastAutoTable.finalY;
    
    if (equity.length > 0) {
      autoTable(doc, {
        startY: startY,
        body: equity.map(acc => [acc.account_name || acc.name || 'Unknown', formatCurrency(acc.balance || 0)]),
        theme: 'striped',
        styles: { fontSize: 9, textColor: PDF_COLORS.bodyText },
        alternateRowStyles: { fillColor: PDF_COLORS.altRowBg },
        columnStyles: { 
          0: { cellWidth: COLUMN_WIDTHS.accountName }, 
          1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
        }
      });
      startY = doc.lastAutoTable.finalY;
    }
    
    // Total Equity
    autoTable(doc, {
      startY: startY,
      body: [['Total Equity', formatCurrency(totalEquity)]],
      theme: 'plain',
      styles: { fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fillColor: PDF_COLORS.totalBg, textColor: PDF_COLORS.totalText },
      columnStyles: { 
        0: { cellWidth: COLUMN_WIDTHS.accountName }, 
        1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
      }
    });
    startY = doc.lastAutoTable.finalY + 8;
    
    // TOTAL LIABILITIES & EQUITY
    autoTable(doc, {
      startY: startY,
      body: [['TOTAL LIABILITIES & EQUITY', formatCurrency(totalLiabilities + totalEquity)]],
      theme: 'plain',
      styles: { fontStyle: 'bold', fontSize: 11 },
      bodyStyles: { fillColor: PDF_COLORS.grandTotalBg, textColor: PDF_COLORS.grandTotalText },
      columnStyles: { 
        0: { cellWidth: COLUMN_WIDTHS.accountName }, 
        1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
      }
    });
    
    // Open PDF in new tab
    const filename = `BalanceSheet_${formatDateForFilename(asOfDate)}.pdf`;
    return savePDF(doc, filename);
  } catch (error) {
    console.error('PDF Export Error:', error);
    alert('Failed to export PDF: ' + error.message);
    return false;
  }
};

/**
 * Export AR/AP Aging to PDF - Fixed column widths
 */
export const exportAgingToPDF = ({
  companyName,
  reportTitle,
  asOfDate,
  data = [],
  totals = {},
  type = 'ar'
}) => {
  try {
    const doc = new jsPDF({ orientation: 'landscape' });
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...PDF_COLORS.bodyText);
    doc.text(companyName || 'Company', 14, 20);
    
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text(reportTitle || (type === 'ar' ? 'Accounts Receivable Aging' : 'Accounts Payable Aging'), 14, 30);
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`As of ${formatDate(asOfDate)}`, 14, 38);
    
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - 60, 20);
    doc.setTextColor(...PDF_COLORS.bodyText);
    
    const headers = [type === 'ar' ? 'Customer' : 'Vendor', 'Current', '1-30 Days', '31-60 Days', '61-90 Days', '90+ Days', 'Total'];
    
    const tableData = data.map(item => [
      item.customer_name || item.vendor_name || 'Unknown',
      formatCurrency(item.current || 0),
      formatCurrency(item['1_30'] || 0),
      formatCurrency(item['31_60'] || 0),
      formatCurrency(item['61_90'] || 0),
      formatCurrency(item.over_90 || 0),
      formatCurrency(item.total || 0)
    ]);
    
    // Add totals row
    tableData.push([
      'TOTAL',
      formatCurrency(totals.current || 0),
      formatCurrency(totals['1_30'] || 0),
      formatCurrency(totals['31_60'] || 0),
      formatCurrency(totals['61_90'] || 0),
      formatCurrency(totals.over_90 || 0),
      formatCurrency(totals.total || 0)
    ]);
    
    autoTable(doc, {
      startY: 45,
      head: [headers],
      body: tableData.slice(0, -1),
      foot: [tableData[tableData.length - 1]],
      theme: 'striped',
      styles: { fontSize: 9, textColor: PDF_COLORS.bodyText, cellPadding: 4 },
      headStyles: { 
        fillColor: PDF_COLORS.headerBg, 
        textColor: PDF_COLORS.headerText, 
        fontStyle: 'bold' 
      },
      footStyles: { 
        fillColor: PDF_COLORS.totalBg, 
        textColor: PDF_COLORS.totalText, 
        fontStyle: 'bold' 
      },
      alternateRowStyles: { fillColor: PDF_COLORS.altRowBg },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: 35, halign: 'right' },
        2: { cellWidth: 35, halign: 'right' },
        3: { cellWidth: 35, halign: 'right' },
        4: { cellWidth: 35, halign: 'right' },
        5: { cellWidth: 35, halign: 'right' },
        6: { cellWidth: 40, halign: 'right', fontStyle: 'bold' }
      },
      margin: { left: 14, right: 14 }
    });
    
    // Open PDF in new tab
    const filename = `${type.toUpperCase()}Aging_${formatDateForFilename(asOfDate)}.pdf`;
    return savePDF(doc, filename);
  } catch (error) {
    console.error('PDF Export Error:', error);
    alert('Failed to export PDF: ' + error.message);
    return false;
  }
};

/**
 * Export Trial Balance to PDF - Fixed colors for readability
 */
export const exportTrialBalanceToPDF = ({
  companyName,
  asOfDate,
  accounts = [],
  totalDebits = 0,
  totalCredits = 0
}) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...PDF_COLORS.bodyText);
    doc.text(companyName || 'Company', 14, 20);
    
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Trial Balance', 14, 30);
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`As of ${formatDate(asOfDate)}`, 14, 38);
    
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - 60, 20);
    doc.setTextColor(...PDF_COLORS.bodyText);
    
    const headers = ['Code', 'Account Name', 'Debit', 'Credit'];
    
    const tableData = accounts.map(acc => [
      acc.account_code || acc.code || '',
      acc.account_name || acc.name || 'Unknown',
      (acc.ending_debit || acc.debit_balance || acc.debit || 0) > 0 ? formatCurrency(acc.ending_debit || acc.debit_balance || acc.debit) : '',
      (acc.ending_credit || acc.credit_balance || acc.credit || 0) > 0 ? formatCurrency(acc.ending_credit || acc.credit_balance || acc.credit) : ''
    ]);
    
    autoTable(doc, {
      startY: 45,
      head: [headers],
      body: tableData,
      foot: [[
        '',
        { content: 'TOTALS', styles: { fontStyle: 'bold' } },
        { content: formatCurrency(totalDebits), styles: { fontStyle: 'bold' } },
        { content: formatCurrency(totalCredits), styles: { fontStyle: 'bold' } }
      ]],
      theme: 'striped',
      styles: { fontSize: 9, textColor: PDF_COLORS.bodyText, cellPadding: 4 },
      headStyles: { 
        fillColor: PDF_COLORS.headerBg, 
        textColor: PDF_COLORS.headerText, 
        fontStyle: 'bold' 
      },
      footStyles: { 
        fillColor: PDF_COLORS.totalBg, 
        textColor: PDF_COLORS.totalText, 
        fontStyle: 'bold' 
      },
      alternateRowStyles: { fillColor: PDF_COLORS.altRowBg },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 95 },
        2: { cellWidth: 35, halign: 'right' },
        3: { cellWidth: 35, halign: 'right' }
      }
    });
    
    // Open PDF in new tab
    const filename = `TrialBalance_${formatDateForFilename(asOfDate)}.pdf`;
    return savePDF(doc, filename);
  } catch (error) {
    console.error('PDF Export Error:', error);
    alert('Failed to export PDF: ' + error.message);
    return false;
  }
};

/**
 * Export Cash Flow Statement to PDF
 */
export const exportCashFlowToPDF = ({
  companyName,
  startDate,
  endDate,
  operating = [],
  investing = [],
  financing = [],
  netOperating = 0,
  netInvesting = 0,
  netFinancing = 0,
  netChange = 0
}) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...PDF_COLORS.bodyText);
    doc.text(companyName || 'Company', 14, 20);
    
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Cash Flow Statement', 14, 30);
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`${formatDate(startDate)} to ${formatDate(endDate)}`, 14, 38);
    
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - 60, 20);
    doc.setTextColor(...PDF_COLORS.bodyText);
    
    let startY = 48;
    
    // Column headers
    autoTable(doc, {
      startY: startY,
      head: [['Description', 'Amount']],
      body: [],
      theme: 'plain',
      headStyles: { 
        fillColor: PDF_COLORS.headerBg, 
        textColor: PDF_COLORS.headerText, 
        fontStyle: 'bold' 
      },
      columnStyles: { 
        0: { cellWidth: COLUMN_WIDTHS.accountName }, 
        1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
      }
    });
    startY = doc.lastAutoTable.finalY;
    
    // Operating Activities
    autoTable(doc, {
      startY: startY,
      body: [['OPERATING ACTIVITIES', '']],
      theme: 'plain',
      styles: { fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fillColor: PDF_COLORS.sectionBg, textColor: PDF_COLORS.sectionText },
      columnStyles: { 0: { cellWidth: COLUMN_WIDTHS.total } }
    });
    startY = doc.lastAutoTable.finalY;
    
    if (operating.length > 0) {
      autoTable(doc, {
        startY: startY,
        body: operating.map(item => [item.description || item.name || '', formatCurrency(item.amount || 0)]),
        theme: 'striped',
        styles: { fontSize: 9, textColor: PDF_COLORS.bodyText },
        alternateRowStyles: { fillColor: PDF_COLORS.altRowBg },
        columnStyles: { 
          0: { cellWidth: COLUMN_WIDTHS.accountName }, 
          1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
        }
      });
      startY = doc.lastAutoTable.finalY;
    }
    
    autoTable(doc, {
      startY: startY,
      body: [['Net Cash from Operations', formatCurrency(netOperating)]],
      theme: 'plain',
      styles: { fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fillColor: PDF_COLORS.totalBg, textColor: PDF_COLORS.totalText },
      columnStyles: { 
        0: { cellWidth: COLUMN_WIDTHS.accountName }, 
        1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
      }
    });
    startY = doc.lastAutoTable.finalY + 3;
    
    // Investing Activities
    autoTable(doc, {
      startY: startY,
      body: [['INVESTING ACTIVITIES', '']],
      theme: 'plain',
      styles: { fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fillColor: PDF_COLORS.sectionBg, textColor: PDF_COLORS.sectionText },
      columnStyles: { 0: { cellWidth: COLUMN_WIDTHS.total } }
    });
    startY = doc.lastAutoTable.finalY;
    
    if (investing.length > 0) {
      autoTable(doc, {
        startY: startY,
        body: investing.map(item => [item.description || item.name || '', formatCurrency(item.amount || 0)]),
        theme: 'striped',
        styles: { fontSize: 9, textColor: PDF_COLORS.bodyText },
        alternateRowStyles: { fillColor: PDF_COLORS.altRowBg },
        columnStyles: { 
          0: { cellWidth: COLUMN_WIDTHS.accountName }, 
          1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
        }
      });
      startY = doc.lastAutoTable.finalY;
    }
    
    autoTable(doc, {
      startY: startY,
      body: [['Net Cash from Investing', formatCurrency(netInvesting)]],
      theme: 'plain',
      styles: { fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fillColor: PDF_COLORS.totalBg, textColor: PDF_COLORS.totalText },
      columnStyles: { 
        0: { cellWidth: COLUMN_WIDTHS.accountName }, 
        1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
      }
    });
    startY = doc.lastAutoTable.finalY + 3;
    
    // Financing Activities
    autoTable(doc, {
      startY: startY,
      body: [['FINANCING ACTIVITIES', '']],
      theme: 'plain',
      styles: { fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fillColor: PDF_COLORS.sectionBg, textColor: PDF_COLORS.sectionText },
      columnStyles: { 0: { cellWidth: COLUMN_WIDTHS.total } }
    });
    startY = doc.lastAutoTable.finalY;
    
    if (financing.length > 0) {
      autoTable(doc, {
        startY: startY,
        body: financing.map(item => [item.description || item.name || '', formatCurrency(item.amount || 0)]),
        theme: 'striped',
        styles: { fontSize: 9, textColor: PDF_COLORS.bodyText },
        alternateRowStyles: { fillColor: PDF_COLORS.altRowBg },
        columnStyles: { 
          0: { cellWidth: COLUMN_WIDTHS.accountName }, 
          1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
        }
      });
      startY = doc.lastAutoTable.finalY;
    }
    
    autoTable(doc, {
      startY: startY,
      body: [['Net Cash from Financing', formatCurrency(netFinancing)]],
      theme: 'plain',
      styles: { fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fillColor: PDF_COLORS.totalBg, textColor: PDF_COLORS.totalText },
      columnStyles: { 
        0: { cellWidth: COLUMN_WIDTHS.accountName }, 
        1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
      }
    });
    startY = doc.lastAutoTable.finalY + 8;
    
    // Net Change in Cash
    autoTable(doc, {
      startY: startY,
      body: [['NET CHANGE IN CASH', formatCurrency(netChange)]],
      theme: 'plain',
      styles: { fontStyle: 'bold', fontSize: 11 },
      bodyStyles: { fillColor: PDF_COLORS.grandTotalBg, textColor: PDF_COLORS.grandTotalText },
      columnStyles: { 
        0: { cellWidth: COLUMN_WIDTHS.accountName }, 
        1: { cellWidth: COLUMN_WIDTHS.amount, halign: 'right' } 
      }
    });
    
    // Open PDF in new tab
    const filename = `CashFlow_${formatDateForFilename(startDate)}_${formatDateForFilename(endDate)}.pdf`;
    return savePDF(doc, filename);
  } catch (error) {
    console.error('PDF Export Error:', error);
    alert('Failed to export PDF: ' + error.message);
    return false;
  }
};

/**
 * Export General Ledger to PDF
 */
export const exportGeneralLedgerToPDF = ({
  companyName,
  startDate,
  endDate,
  entries = []
}) => {
  try {
    const doc = new jsPDF({ orientation: 'landscape' });
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...PDF_COLORS.bodyText);
    doc.text(companyName || 'Company', 14, 20);
    
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('General Ledger', 14, 30);
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`${formatDate(startDate)} to ${formatDate(endDate)}`, 14, 38);
    
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - 60, 20);
    doc.setTextColor(...PDF_COLORS.bodyText);
    
    const headers = ['Date', 'Entry #', 'Account', 'Description', 'Debit', 'Credit'];
    
    const tableData = entries.map(e => [
      e.txn_date || '',
      e.entry_no || '',
      `${e.account_code || ''} - ${e.account_name || ''}`,
      e.memo || e.description || '',
      (e.debit || 0) > 0 ? formatCurrency(e.debit) : '',
      (e.credit || 0) > 0 ? formatCurrency(e.credit) : ''
    ]);
    
    autoTable(doc, {
      startY: 45,
      head: [headers],
      body: tableData,
      theme: 'striped',
      styles: { fontSize: 8, textColor: PDF_COLORS.bodyText, cellPadding: 3 },
      headStyles: { 
        fillColor: PDF_COLORS.headerBg, 
        textColor: PDF_COLORS.headerText, 
        fontStyle: 'bold' 
      },
      alternateRowStyles: { fillColor: PDF_COLORS.altRowBg },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 20 },
        2: { cellWidth: 70 },
        3: { cellWidth: 'auto' },
        4: { cellWidth: 30, halign: 'right' },
        5: { cellWidth: 30, halign: 'right' }
      },
      margin: { left: 14, right: 14 }
    });
    
    // Open PDF in new tab
    const filename = `GeneralLedger_${formatDateForFilename(startDate)}_${formatDateForFilename(endDate)}.pdf`;
    return savePDF(doc, filename);
  } catch (error) {
    console.error('PDF Export Error:', error);
    alert('Failed to export PDF: ' + error.message);
    return false;
  }
};

/**
 * Export Budget vs Actual to PDF
 */
export const exportBudgetActualToPDF = ({
  companyName,
  startDate,
  endDate,
  data = []
}) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...PDF_COLORS.bodyText);
    doc.text(companyName || 'Company', 14, 20);
    
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Budget vs Actual', 14, 30);
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`${formatDate(startDate)} to ${formatDate(endDate)}`, 14, 38);
    
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - 60, 20);
    doc.setTextColor(...PDF_COLORS.bodyText);
    
    const headers = ['Account', 'Budget', 'Actual', 'Variance', '%'];
    
    const tableData = data.map(item => [
      item.account_name || item.name || 'Unknown',
      formatCurrency(item.budget || 0),
      formatCurrency(item.actual || 0),
      formatCurrency(item.variance || 0),
      `${((item.variance_pct || 0) * 100).toFixed(1)}%`
    ]);
    
    autoTable(doc, {
      startY: 45,
      head: [headers],
      body: tableData,
      theme: 'striped',
      styles: { fontSize: 9, textColor: PDF_COLORS.bodyText, cellPadding: 4 },
      headStyles: { 
        fillColor: PDF_COLORS.headerBg, 
        textColor: PDF_COLORS.headerText, 
        fontStyle: 'bold' 
      },
      alternateRowStyles: { fillColor: PDF_COLORS.altRowBg },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: 32, halign: 'right' },
        2: { cellWidth: 32, halign: 'right' },
        3: { cellWidth: 32, halign: 'right' },
        4: { cellWidth: 22, halign: 'right' }
      }
    });
    
    // Open PDF in new tab
    const filename = `BudgetVsActual_${formatDateForFilename(startDate)}_${formatDateForFilename(endDate)}.pdf`;
    return savePDF(doc, filename);
  } catch (error) {
    console.error('PDF Export Error:', error);
    alert('Failed to export PDF: ' + error.message);
    return false;
  }
};

/**
 * Export Expense by Vendor to PDF
 */
export const exportExpenseByVendorToPDF = ({
  companyName,
  startDate,
  endDate,
  data = [],
  total = 0
}) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...PDF_COLORS.bodyText);
    doc.text(companyName || 'Company', 14, 20);
    
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Expense by Vendor', 14, 30);
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`${formatDate(startDate)} to ${formatDate(endDate)}`, 14, 38);
    
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - 60, 20);
    doc.setTextColor(...PDF_COLORS.bodyText);
    
    const headers = ['Vendor', 'Amount', '% of Total'];
    
    const tableData = data.map(item => [
      item.vendor_name || item.name || 'Unknown',
      formatCurrency(item.amount || item.total || 0),
      `${((item.percentage || 0) * 100).toFixed(1)}%`
    ]);
    
    autoTable(doc, {
      startY: 45,
      head: [headers],
      body: tableData,
      foot: [[
        { content: 'TOTAL', styles: { fontStyle: 'bold' } },
        { content: formatCurrency(total), styles: { fontStyle: 'bold' } },
        { content: '100%', styles: { fontStyle: 'bold' } }
      ]],
      theme: 'striped',
      styles: { fontSize: 9, textColor: PDF_COLORS.bodyText, cellPadding: 4 },
      headStyles: { 
        fillColor: PDF_COLORS.headerBg, 
        textColor: PDF_COLORS.headerText, 
        fontStyle: 'bold' 
      },
      footStyles: { 
        fillColor: PDF_COLORS.totalBg, 
        textColor: PDF_COLORS.totalText, 
        fontStyle: 'bold' 
      },
      alternateRowStyles: { fillColor: PDF_COLORS.altRowBg },
      columnStyles: {
        0: { cellWidth: 100 },
        1: { cellWidth: 45, halign: 'right' },
        2: { cellWidth: 30, halign: 'right' }
      }
    });
    
    // Open PDF in new tab
    const filename = `ExpenseByVendor_${formatDateForFilename(startDate)}_${formatDateForFilename(endDate)}.pdf`;
    return savePDF(doc, filename);
  } catch (error) {
    console.error('PDF Export Error:', error);
    alert('Failed to export PDF: ' + error.message);
    return false;
  }
};

/**
 * Export generic table to PDF
 */
export const exportTableToPDF = ({
  title,
  subtitle,
  companyName,
  headers = [],
  data = [],
  filename,
  orientation = 'portrait'
}) => {
  try {
    const doc = new jsPDF({ orientation });
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...PDF_COLORS.bodyText);
    doc.text(companyName || 'Company', 14, 20);
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(title || 'Report', 14, 28);
    
    if (subtitle) {
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(subtitle, 14, 36);
    }
    
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - 60, 20);
    doc.setTextColor(...PDF_COLORS.bodyText);
    
    autoTable(doc, {
      startY: subtitle ? 42 : 34,
      head: [headers],
      body: data,
      theme: 'striped',
      styles: { fontSize: 9, textColor: PDF_COLORS.bodyText },
      headStyles: { 
        fillColor: PDF_COLORS.headerBg, 
        textColor: PDF_COLORS.headerText, 
        fontStyle: 'bold' 
      },
      alternateRowStyles: { fillColor: PDF_COLORS.altRowBg },
      margin: { left: 14, right: 14 }
    });
    
    // Open PDF in new tab
    const fname = `${filename || 'Report'}.pdf`;
    return savePDF(doc, fname);
  } catch (error) {
    console.error('PDF Export Error:', error);
    alert('Failed to export PDF: ' + error.message);
    return false;
  }
};

// Excel export functions - use XLSX.writeFile which triggers browser save dialog
export const exportTableToExcel = ({
  title,
  subtitle,
  headers = [],
  data = [],
  filename,
  sheetName = 'Sheet1',
  currencyColumns = []
}) => {
  try {
    const wsData = [
      [title || 'Report'],
      subtitle ? [subtitle] : [],
      [],
      headers,
      ...data
    ].filter(row => row.length > 0);
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    if (headers.length > 0) {
      ws['!cols'] = headers.map((h, i) => {
        const maxLen = Math.max(
          String(h).length,
          ...data.map(row => String(row[i] || '').length)
        );
        return { wch: Math.min(Math.max(maxLen + 2, 10), 50) };
      });
    }
    
    if (currencyColumns.length > 0) {
      applyCurrencyFormat(ws, currencyColumns, 4);
    }
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    
    // Use XLSX.writeFile which triggers browser save dialog
    const fname = `${filename || 'Report'}.xlsx`;
    saveExcelFile(wb, fname);
    return true;
  } catch (error) {
    console.error('Excel Export Error:', error);
    alert('Failed to export Excel file: ' + error.message);
    return false;
  }
};

export const exportPnLToExcel = ({
  companyName,
  startDate,
  endDate,
  basis,
  incomeAccounts = [],
  expenseAccounts = [],
  totalIncome = 0,
  totalExpenses = 0,
  netIncome = 0
}) => {
  try {
    const wsData = [
      [companyName || 'Company'],
      ['Profit & Loss Statement'],
      [`${formatDate(startDate)} to ${formatDate(endDate)}`],
      [`Basis: ${basis === 'cash' ? 'Cash' : 'Accrual'}`],
      [],
      ['Account', 'Amount'],
      [],
      ['INCOME'],
      ...incomeAccounts.map(acc => [acc.account_name || acc.name || 'Unknown', acc.balance || acc.amount || 0]),
      ['Total Income', totalIncome],
      [],
      ['EXPENSES'],
      ...expenseAccounts.map(acc => [acc.account_name || acc.name || 'Unknown', acc.balance || acc.amount || 0]),
      ['Total Expenses', totalExpenses],
      [],
      ['NET INCOME', netIncome]
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 40 }, { wch: 18 }];
    applyCurrencyFormat(ws, [1], 7);
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Profit & Loss');
    
    // Use XLSX.writeFile which triggers browser save dialog
    const filename = `ProfitLoss_${formatDateForFilename(startDate)}_${formatDateForFilename(endDate)}.xlsx`;
    saveExcelFile(wb, filename);
    return true;
  } catch (error) {
    console.error('Excel Export Error:', error);
    alert('Failed to export Excel file: ' + error.message);
    return false;
  }
};

export const exportBalanceSheetToExcel = ({
  companyName,
  asOfDate,
  basis,
  assets = [],
  liabilities = [],
  equity = [],
  totalAssets = 0,
  totalLiabilities = 0,
  totalEquity = 0
}) => {
  try {
    const wsData = [
      [companyName || 'Company'],
      ['Balance Sheet'],
      [`As of ${formatDate(asOfDate)}`],
      [`Basis: ${basis === 'cash' ? 'Cash' : 'Accrual'}`],
      [],
      ['Account', 'Balance'],
      [],
      ['ASSETS'],
      ...assets.map(acc => [acc.account_name || acc.name || 'Unknown', acc.balance || 0]),
      ['Total Assets', totalAssets],
      [],
      ['LIABILITIES'],
      ...liabilities.map(acc => [acc.account_name || acc.name || 'Unknown', acc.balance || 0]),
      ['Total Liabilities', totalLiabilities],
      [],
      ['EQUITY'],
      ...equity.map(acc => [acc.account_name || acc.name || 'Unknown', acc.balance || 0]),
      ['Total Equity', totalEquity],
      [],
      ['TOTAL LIABILITIES & EQUITY', totalLiabilities + totalEquity]
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 40 }, { wch: 18 }];
    applyCurrencyFormat(ws, [1], 7);
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Balance Sheet');
    
    // Use XLSX.writeFile which triggers browser save dialog
    const filename = `BalanceSheet_${formatDateForFilename(asOfDate)}.xlsx`;
    saveExcelFile(wb, filename);
    return true;
  } catch (error) {
    console.error('Excel Export Error:', error);
    alert('Failed to export Excel file: ' + error.message);
    return false;
  }
};

export const exportAgingToExcel = ({
  companyName,
  reportTitle,
  asOfDate,
  data = [],
  totals = {},
  type = 'ar'
}) => {
  try {
    const nameCol = type === 'ar' ? 'Customer' : 'Vendor';
    const wsData = [
      [companyName || 'Company'],
      [reportTitle || (type === 'ar' ? 'Accounts Receivable Aging' : 'Accounts Payable Aging')],
      [`As of ${formatDate(asOfDate)}`],
      [],
      [nameCol, 'Current', '1-30 Days', '31-60 Days', '61-90 Days', '90+ Days', 'Total'],
      ...data.map(item => [
        item.customer_name || item.vendor_name || 'Unknown',
        item.current || 0,
        item['1_30'] || 0,
        item['31_60'] || 0,
        item['61_90'] || 0,
        item.over_90 || 0,
        item.total || 0
      ]),
      ['TOTAL', totals.current || 0, totals['1_30'] || 0, totals['31_60'] || 0, totals['61_90'] || 0, totals.over_90 || 0, totals.total || 0]
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 30 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
    applyCurrencyFormat(ws, [1, 2, 3, 4, 5, 6], 4);
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `${type.toUpperCase()} Aging`);
    
    // Use XLSX.writeFile which triggers browser save dialog
    const filename = `${type.toUpperCase()}Aging_${formatDateForFilename(asOfDate)}.xlsx`;
    saveExcelFile(wb, filename);
    return true;
  } catch (error) {
    console.error('Excel Export Error:', error);
    alert('Failed to export Excel file: ' + error.message);
    return false;
  }
};

export const exportTrialBalanceToExcel = ({
  companyName,
  asOfDate,
  accounts = [],
  totalDebits = 0,
  totalCredits = 0
}) => {
  try {
    const wsData = [
      [companyName || 'Company'],
      ['Trial Balance'],
      [`As of ${formatDate(asOfDate)}`],
      [],
      ['Code', 'Account Name', 'Debit', 'Credit'],
      ...accounts.map(acc => [
        acc.account_code || acc.code || '',
        acc.account_name || acc.name || 'Unknown',
        (acc.ending_debit || acc.debit_balance || acc.debit || 0) > 0 ? (acc.ending_debit || acc.debit_balance || acc.debit) : null,
        (acc.ending_credit || acc.credit_balance || acc.credit || 0) > 0 ? (acc.ending_credit || acc.credit_balance || acc.credit) : null
      ]),
      ['', 'TOTALS', totalDebits, totalCredits]
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 12 }, { wch: 40 }, { wch: 16 }, { wch: 16 }];
    applyCurrencyFormat(ws, [2, 3], 4);
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Trial Balance');
    
    // Use XLSX.writeFile which triggers browser save dialog
    const filename = `TrialBalance_${formatDateForFilename(asOfDate)}.xlsx`;
    saveExcelFile(wb, filename);
    return true;
  } catch (error) {
    console.error('Excel Export Error:', error);
    alert('Failed to export Excel file: ' + error.message);
    return false;
  }
};

export const exportGeneralLedgerToExcel = ({
  companyName,
  startDate,
  endDate,
  entries = [],
  filename
}) => {
  try {
    const wsData = [
      [companyName || 'Company'],
      ['General Ledger'],
      [`${formatDate(startDate)} to ${formatDate(endDate)}`],
      [],
      ['Date', 'Entry #', 'Account', 'Description', 'Debit', 'Credit'],
      ...entries.map(entry => [
        entry.txn_date || '',
        entry.entry_no || '',
        `${entry.account_code || ''} - ${entry.account_name || ''}`,
        entry.memo || entry.description || '',
        (entry.debit || 0) > 0 ? entry.debit : null,
        (entry.credit || 0) > 0 ? entry.credit : null
      ])
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 12 }, { wch: 10 }, { wch: 35 }, { wch: 30 }, { wch: 14 }, { wch: 14 }];
    applyCurrencyFormat(ws, [4, 5], 4);
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'General Ledger');
    
    // Use XLSX.writeFile which triggers browser save dialog
    const fname = filename || `GeneralLedger_${formatDateForFilename(startDate)}_${formatDateForFilename(endDate)}.xlsx`;
    saveExcelFile(wb, fname);
    return true;
  } catch (error) {
    console.error('Excel Export Error:', error);
    alert('Failed to export Excel file: ' + error.message);
    return false;
  }
};

export const exportCashFlowToExcel = ({
  companyName,
  startDate,
  endDate,
  operating = [],
  investing = [],
  financing = [],
  netOperating = 0,
  netInvesting = 0,
  netFinancing = 0,
  netChange = 0
}) => {
  try {
    const wsData = [
      [companyName || 'Company'],
      ['Cash Flow Statement'],
      [`${formatDate(startDate)} to ${formatDate(endDate)}`],
      [],
      ['Description', 'Amount'],
      [],
      ['OPERATING ACTIVITIES'],
      ...operating.map(item => [item.description || item.name || '', item.amount || 0]),
      ['Net Cash from Operations', netOperating],
      [],
      ['INVESTING ACTIVITIES'],
      ...investing.map(item => [item.description || item.name || '', item.amount || 0]),
      ['Net Cash from Investing', netInvesting],
      [],
      ['FINANCING ACTIVITIES'],
      ...financing.map(item => [item.description || item.name || '', item.amount || 0]),
      ['Net Cash from Financing', netFinancing],
      [],
      ['NET CHANGE IN CASH', netChange]
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 40 }, { wch: 18 }];
    applyCurrencyFormat(ws, [1], 6);
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cash Flow');
    
    // Use XLSX.writeFile which triggers browser save dialog
    const filename = `CashFlow_${formatDateForFilename(startDate)}_${formatDateForFilename(endDate)}.xlsx`;
    saveExcelFile(wb, filename);
    return true;
  } catch (error) {
    console.error('Excel Export Error:', error);
    alert('Failed to export Excel file: ' + error.message);
    return false;
  }
};

export const exportBudgetActualToExcel = ({
  companyName,
  startDate,
  endDate,
  data = []
}) => {
  try {
    const wsData = [
      [companyName || 'Company'],
      ['Budget vs Actual'],
      [`${formatDate(startDate)} to ${formatDate(endDate)}`],
      [],
      ['Account', 'Budget', 'Actual', 'Variance', 'Variance %'],
      ...data.map(item => [
        item.account_name || item.name || 'Unknown',
        item.budget || 0,
        item.actual || 0,
        item.variance || 0,
        (item.variance_pct || 0) * 100
      ])
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 40 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 12 }];
    applyCurrencyFormat(ws, [1, 2, 3], 4);
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Budget vs Actual');
    
    // Use XLSX.writeFile which triggers browser save dialog
    const filename = `BudgetVsActual_${formatDateForFilename(startDate)}_${formatDateForFilename(endDate)}.xlsx`;
    saveExcelFile(wb, filename);
    return true;
  } catch (error) {
    console.error('Excel Export Error:', error);
    alert('Failed to export Excel file: ' + error.message);
    return false;
  }
};

export const exportExpenseByVendorToExcel = ({
  companyName,
  startDate,
  endDate,
  data = [],
  total = 0
}) => {
  try {
    const wsData = [
      [companyName || 'Company'],
      ['Expense by Vendor'],
      [`${formatDate(startDate)} to ${formatDate(endDate)}`],
      [],
      ['Vendor', 'Amount', '% of Total'],
      ...data.map(item => [
        item.vendor_name || item.name || 'Unknown',
        item.amount || item.total || 0,
        (item.percentage || 0) * 100
      ]),
      ['TOTAL', total, 100]
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 40 }, { wch: 16 }, { wch: 12 }];
    applyCurrencyFormat(ws, [1], 4);
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Expense by Vendor');
    
    // Use XLSX.writeFile which triggers browser save dialog
    const filename = `ExpenseByVendor_${formatDateForFilename(startDate)}_${formatDateForFilename(endDate)}.xlsx`;
    saveExcelFile(wb, filename);
    return true;
  } catch (error) {
    console.error('Excel Export Error:', error);
    alert('Failed to export Excel file: ' + error.message);
    return false;
  }
};

/**
 * Export data as CSV file
 * Handles proper escaping of commas, quotes, and newlines
 */
export const exportToCSV = ({ headers = [], data = [], filename = 'export.csv', title = '', subtitle = '' }) => {
  try {
    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = [];
    if (title) rows.push([escapeCSV(title)]);
    if (subtitle) rows.push([escapeCSV(subtitle)]);
    if (title || subtitle) rows.push([]);

    if (headers.length > 0) {
      rows.push(headers.map(escapeCSV).join(','));
    }

    data.forEach(row => {
      if (Array.isArray(row)) {
        rows.push(row.map(escapeCSV).join(','));
      }
    });

    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    forceDownload(blob, filename);
    return true;
  } catch (error) {
    console.error('CSV Export Error:', error);
    alert('Failed to export CSV: ' + error.message);
    return false;
  }
};

/**
 * Generic report-to-CSV converter: takes report shape and produces CSV
 */
export const exportReportToCSV = ({ companyName, reportTitle, dateLabel, headers, rows, filename }) => {
  return exportToCSV({
    title: companyName || 'Company',
    subtitle: `${reportTitle || 'Report'} — ${dateLabel || ''}`,
    headers,
    data: rows,
    filename: filename || 'report.csv'
  });
};

/**
 * Print/generate PDF from HTML content.
 * - Deployed site: Opens a hidden iframe, injects HTML, calls window.print()
 * - Preview iframe: Generates PDF via backend WeasyPrint, shows PDF viewer
 */
export const printContent = (bodyHtml, options = {}) => {
  const { filename = 'CountBucks_Document.pdf' } = options;

  if (!isEmbedded()) {
    // Direct print — works on deployed sites (no sandbox restrictions)
    const fullHtml = `<!DOCTYPE html><html><head>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 20px; color: #1f2937; font-size: 11px; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #374151; color: white; padding: 8px 10px; text-align: left; }
        td { padding: 6px 10px; border-bottom: 1px solid #e5e7eb; }
        @page { margin: 0.5in; }
      </style>
    </head><body>${bodyHtml}</body></html>`;

    const printFrame = document.createElement('iframe');
    printFrame.style.cssText = 'position:fixed;left:-9999px;width:0;height:0;border:none;';
    document.body.appendChild(printFrame);
    printFrame.contentDocument.open();
    printFrame.contentDocument.write(fullHtml);
    printFrame.contentDocument.close();
    printFrame.contentWindow.focus();
    printFrame.contentWindow.print();
    setTimeout(() => document.body.removeChild(printFrame), 2000);
    return;
  }

  // Sandboxed iframe — generate PDF on backend and show viewer
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  fetch(`${API_URL}/api/generate-pdf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ html: bodyHtml, filename })
  })
  .then(response => {
    if (!response.ok) throw new Error(`Server error: ${response.status}`);
    return response.blob();
  })
  .then(blob => {
    const pdfBlob = new Blob([blob], { type: 'application/pdf' });
    forceDownload(pdfBlob, filename);
  })
  .catch(err => {
    console.error('PDF generation failed:', err);
    alert('Failed to generate PDF: ' + err.message);
  });
};

/**
 * Print report - opens Blob URL window with formatted report content
 */
export const printReport = ({
  title,
  subtitle,
  companyName,
  contentHtml
}) => {
  const bodyHtml = `
  <style>
    .report-body { font-size: 11px; line-height: 1.4; color: #1f2937; }
    .report-body .header { margin-bottom: 25px; border-bottom: 2px solid #1f2937; padding-bottom: 15px; }
    .report-body .company-name { font-size: 22px; font-weight: bold; margin-bottom: 5px; color: #1f2937; }
    .report-body .report-title { font-size: 16px; font-weight: bold; margin-bottom: 5px; color: #1f2937; }
    .report-body .report-subtitle { font-size: 11px; color: #6b7280; }
    .report-body table { width: 100%; border-collapse: collapse; margin-top: 15px; }
    .report-body th { background: #374151; color: white; padding: 10px 12px; text-align: left; font-weight: 600; font-size: 11px; }
    .report-body td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-size: 11px; }
    .report-body tr:nth-child(even) { background: #f9fafb; }
    .report-body .section-header td { background: #4b5563; color: white; font-weight: bold; font-size: 12px; }
    .report-body .total-row td { background: #f3f4f6; font-weight: bold; border-top: 1px solid #d1d5db; }
    .report-body .grand-total td { background: #1f2937; color: white; font-weight: bold; font-size: 12px; }
    .report-body .text-right { text-align: right; }
    .report-body .font-mono { font-family: ui-monospace, monospace; }
    .report-body .timestamp { font-size: 9px; color: #9ca3af; text-align: right; margin-top: 25px; padding-top: 10px; border-top: 1px solid #e5e7eb; }
  </style>
  <div class="report-body">
    <div class="header">
      <div class="company-name">${companyName || 'Company'}</div>
      <div class="report-title">${title || 'Report'}</div>
      ${subtitle ? `<div class="report-subtitle">${subtitle}</div>` : ''}
    </div>
    ${contentHtml || '<p>No data to display</p>'}
    <div class="timestamp">Generated: ${new Date().toLocaleString()}</div>
  </div>`;

  printContent(bodyHtml, { filename: `${(title || 'Report').replace(/\s+/g, '_')}.pdf` });
  return true;
};

/**
 * Generate HTML table for printing - Fixed data handling
 */
export const generatePrintTableHTML = ({
  headers = [],
  data = [],
  footerRow = null
}) => {
  if (!headers.length && !data.length) {
    return '<p style="color: #1f2937;">No data available</p>';
  }
  
  let html = '<table>';
  
  // Headers
  if (headers.length > 0) {
    html += '<thead><tr>';
    headers.forEach((h, i) => {
      const isNumeric = i > 0;
      html += `<th class="${isNumeric ? 'text-right' : ''}">${h || ''}</th>`;
    });
    html += '</tr></thead>';
  }
  
  // Body
  html += '<tbody>';
  data.forEach((row, rowIdx) => {
    if (!row) return;
    
    const isSection = row.isSection === true;
    const isTotal = row.isTotal === true;
    const cells = row.cells || (Array.isArray(row) ? row : []);
    
    let rowClass = '';
    if (isSection) rowClass = 'section-header';
    else if (isTotal) rowClass = 'total-row';
    else if (rowIdx % 2 === 1) rowClass = 'alt-row';
    
    html += `<tr class="${rowClass}">`;
    if (Array.isArray(cells)) {
      cells.forEach((cell, i) => {
        const isNumeric = i > 0;
        const cellValue = cell !== null && cell !== undefined ? cell : '';
        html += `<td class="${isNumeric ? 'text-right font-mono' : ''}">${cellValue}</td>`;
      });
    }
    html += '</tr>';
  });
  html += '</tbody>';
  
  // Footer
  if (footerRow && Array.isArray(footerRow)) {
    html += '<tfoot><tr class="grand-total">';
    footerRow.forEach((cell, i) => {
      const isNumeric = i > 0;
      html += `<td class="${isNumeric ? 'text-right font-mono' : ''}">${cell || ''}</td>`;
    });
    html += '</tr></tfoot>';
  }
  
  html += '</table>';
  return html;
};
